import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { countryCoordinates } from "./helpers";
import markerUrl from "../../images/marker.svg";

const CustomMap = ({ infoBlock }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const selectedCountryCoordinates = useMemo(() => {
    if (!infoBlock?.length) return null;

    const selectedCountry = infoBlock.find((info) => info.key === "Country");
    if (!selectedCountry) return null;

    const coordinates = Object.entries(countryCoordinates).find(
      ([, country]) => country.name === selectedCountry.label
    );

    if (!coordinates) return null;

    return { ...coordinates[1], code: coordinates[0] };
  }, [infoBlock]);

  const createMap = useCallback(async () => {
    if (!mapRef.current || !selectedCountryCoordinates) return;

    const mapboxgl = (await import("mapbox-gl")).default;
    mapboxgl.accessToken =
      "pk.eyJ1IjoiZHJvc2NhIiwiYSI6ImNtZWNwZWxmODAxcWUyanNnZ24ydWNnZmYifQ.I74IPqX2mXe9gH-B2Rz5gA";

    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    const countryMap = new Map();
    countryMap.set("GB", {
      country: "GB",
      coordinates: countryCoordinates.GB.loc,
    });

    countryMap.set(selectedCountryCoordinates.code, {
      country: selectedCountryCoordinates.code,
      coordinates: selectedCountryCoordinates.loc,
    });

    const countries = Array.from(countryMap.values());
    if (!countries.length) return;

    const coordinates = countries.map((c) => c.coordinates);
    const lngs = coordinates.map((coord) => coord[0]);
    const lats = coordinates.map((coord) => coord[1]);
    const bounds = [
      [Math.min(...lngs) - 5, Math.min(...lats) - 5],
      [Math.max(...lngs) + 5, Math.max(...lats) + 5],
    ];

    const map = new mapboxgl.Map({
      container: mapRef.current,
      style: "mapbox://styles/mapbox/streets-v12",
      projection: "mercator",
      bounds,
      fitBoundsOptions: { padding: 20 },
    });

    map
      .getContainer()
      .querySelectorAll(".mapboxgl-ctrl-attrib, .mapboxgl-ctrl-logo")
      .forEach((el) => el.remove());

    mapInstanceRef.current = map;

    map.on("load", (event) => {
      event.target.getStyle().layers.forEach((layer) => {
        if (layer.type === "symbol") {
          map.setLayoutProperty(layer.id, "visibility", "none");
        }
      });
      map.setLayoutProperty("country-label", "visibility", "visible");
      map.setFilter("country-label", [
        "in",
        ["get", "name_en"],
        [
          "literal",
          countries.map(({ country }) => countryCoordinates[country].name),
        ],
      ]);

      map._customMarkers = countries.map((countryData) => {
        const markerEl = document.createElement("div");
        markerEl.innerHTML = `<img src="${markerUrl}" alt="marker" class="w-auto h-auto cursor-pointer" />`;

        markerEl.addEventListener("click", (e) => {
          e.stopPropagation();
          const [lng, lat] = countryData.coordinates;
          map.easeTo({
            center: [lng, lat],
            zoom: Math.max(map.getZoom(), 5),
            duration: 800,
          });
        });

        const marker = new mapboxgl.Marker({
          element: markerEl,
          anchor: "bottom",
        })
          .setLngLat(countryData.coordinates)
          .addTo(map);

        return { marker, element: markerEl, country: countryData.country };
      });

      map.once("idle", () => {
        if (mapRef.current) mapRef.current.style.visibility = "visible";
      });

      map.addSource("route-line", {
        type: "geojson",
        data: {
          type: "Feature",
          properties: {},
          geometry: {
            type: "LineString",
            coordinates: countries.map(({ coordinates }) => coordinates),
          },
        },
      });

      map.addLayer({
        id: "route-line",
        type: "line",
        source: "route-line",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": "#000000",
          "line-width": 2,
          "line-dasharray": [2, 2],
        },
      });
    });
  }, [selectedCountryCoordinates]);

  useEffect(() => {
    if (!isClient) return;

    if (!document.querySelector('link[href*="mapbox-gl"]')) {
      const cssLink = document.createElement("link");
      cssLink.rel = "stylesheet";
      cssLink.href =
        "https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css";
      document.head.appendChild(cssLink);
    }

    createMap();
  }, [isClient, createMap]);

  if (!isClient || typeof window === "undefined" || !infoBlock) {
    return null;
  }

  return (
    <div
      ref={mapRef}
      id="map"
      style={{
        height: 230,
        border: "1px solid #e3e3e3",
        borderRadius: 20,
        width: "100%",
        margin: "20px 0 30px",
        visibility: "hidden",
      }}
    />
  );
};

export { CustomMap };
